import React, { useRef, useState } from 'react';
import { Button, DatePicker, Input, Spin, Table, Divider } from 'antd';
import { PrinterOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { ReportFilter, DetailItem, SubtotalItem, UnitGrandTotal } from '../../services/d319Api';
import { useD319Report } from './hooks/useD319';
import './D319Form.css';

const { RangePicker } = DatePicker;

const D319Form: React.FC = () => {
  const printRef = useRef<HTMLDivElement>(null);
  const { loading, reportData, queried, noData, runQuery } = useD319Report();

  // Filter state
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [vendorStart, setVendorStart] = useState('');
  const [vendorEnd, setVendorEnd] = useState('');
  const [productStart, setProductStart] = useState('');
  const [productEnd, setProductEnd] = useState('');
  const [processStart, setProcessStart] = useState('');
  const [processEnd, setProcessEnd] = useState('');
  const [deptStart, setDeptStart] = useState('');
  const [deptEnd, setDeptEnd] = useState('');

  const handleQuery = () => {
    const filter: ReportFilter = {
      date_start: dateRange?.[0]?.format('YYYY-MM-DD'),
      date_end: dateRange?.[1]?.format('YYYY-MM-DD'),
      vendor_start: vendorStart || undefined,
      vendor_end: vendorEnd || undefined,
      product_start: productStart || undefined,
      product_end: productEnd || undefined,
      process_start: processStart || undefined,
      process_end: processEnd || undefined,
      dept_start: deptStart || undefined,
      dept_end: deptEnd || undefined,
    };
    runQuery(filter);
  };

  const handlePrint = () => {
    if (!reportData) return;
    const win = window.open('', '_blank');
    if (!win) return;
    const now = new Date().toLocaleString('zh-TW');
    win.document.write(`<!DOCTYPE html><html><head><title>託工入庫統計表</title>
<style>
  body { font-family: 'Microsoft JhengHei', sans-serif; margin: 20px; font-size: 12px; }
  h2 { text-align: center; margin-bottom: 4px; }
  .meta { text-align: center; font-size: 11px; color: #555; margin-bottom: 12px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
  th, td { border: 1px solid #333; padding: 3px 6px; }
  th { background: #f0f0f0; text-align: center; }
  .right { text-align: right; }
  .center { text-align: center; }
  .section-title { font-weight: bold; background: #e8e8e8; }
  @media print { body { margin: 10mm; } }
</style></head><body>
<h2>託工入庫統計表</h2>
<div class="meta">列印時間: ${now}</div>
<table>
<thead>
  <tr class="section-title"><th colspan="7">明細資料</th></tr>
  <tr>
    <th>廠商代號</th><th>工繳代號</th><th>產品代號</th>
    <th>單位</th><th class="right">數量</th><th class="right">單價</th><th class="right">金額</th>
  </tr>
</thead>
<tbody>
${reportData.details.map(r => `<tr>
  <td class="center">${r.vendor_code}</td><td class="center">${r.process_code}</td>
  <td class="center">${(r.color_code1||'').trim()}-${(r.color_code2||'').trim()}</td>
  <td class="center">${r.unit}</td>
  <td class="right">${r.qty.toFixed(2)}</td><td class="right">${r.unit_price.toFixed(3)}</td>
  <td class="right">${r.amount.toFixed(2)}</td>
</tr>`).join('')}
</tbody></table>
<table>
<thead>
  <tr class="section-title"><th colspan="5">廠商小計（依廠商+單位）</th></tr>
  <tr>
    <th>廠商代號</th><th>單位</th>
    <th class="right">總數量</th><th class="right">單價</th><th class="right">總金額</th>
  </tr>
</thead>
<tbody>
${reportData.subtotals.map(r => `<tr>
  <td class="center">${r.vendor_code}</td><td class="center">${r.unit}</td>
  <td class="right">${r.total_qty.toFixed(2)}</td><td class="right">${r.unit_price.toFixed(3)}</td>
  <td class="right">${r.total_amt.toFixed(2)}</td>
</tr>`).join('')}
</tbody></table>
<table>
<thead>
  <tr class="section-title"><th colspan="3">全部合計（依單位）</th></tr>
  <tr><th>單位</th><th class="right">總數量</th><th class="right">總金額</th></tr>
</thead>
<tbody>
${reportData.grand_totals.map(r => `<tr>
  <td class="center">${r.unit}</td>
  <td class="right">${r.total_qty.toFixed(2)}</td><td class="right">${r.total_amt.toFixed(2)}</td>
</tr>`).join('')}
</tbody></table>
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const detailColumns: ColumnsType<DetailItem> = [
    { title: '廠商代號', dataIndex: 'vendor_code', key: 'vendor_code', align: 'center', width: 90 },
    { title: '工繳代號', dataIndex: 'process_code', key: 'process_code', align: 'center', width: 80 },
    { title: '產品代號', key: 'product_code', align: 'center', width: 140, render: (_: unknown, r: DetailItem) => `${(r.color_code1 || '').trim()}-${(r.color_code2 || '').trim()}` },
    { title: '單位', dataIndex: 'unit', key: 'unit', align: 'center', width: 60 },
    { title: '數量', dataIndex: 'qty', key: 'qty', align: 'right', width: 90, render: (v: number) => v.toFixed(2) },
    { title: '單價', dataIndex: 'unit_price', key: 'unit_price', align: 'right', width: 90, render: (v: number) => v.toFixed(3) },
    { title: '金額', dataIndex: 'amount', key: 'amount', align: 'right', width: 100, render: (v: number) => v.toFixed(2) },
  ];

  const subtotalColumns: ColumnsType<SubtotalItem> = [
    { title: '廠商代號', dataIndex: 'vendor_code', key: 'vendor_code', align: 'center', width: 90 },
    { title: '單位', dataIndex: 'unit', key: 'unit', align: 'center', width: 60 },
    { title: '總數量', dataIndex: 'total_qty', key: 'total_qty', align: 'right', width: 100, render: (v: number) => v.toFixed(2) },
    { title: '單價', dataIndex: 'unit_price', key: 'unit_price', align: 'right', width: 100, render: (v: number) => v.toFixed(3) },
    { title: '總金額', dataIndex: 'total_amt', key: 'total_amt', align: 'right', width: 110, render: (v: number) => v.toFixed(2) },
  ];

  const grandTotalColumns: ColumnsType<UnitGrandTotal> = [
    { title: '單位', dataIndex: 'unit', key: 'unit', align: 'center', width: 80 },
    { title: '總數量', dataIndex: 'total_qty', key: 'total_qty', align: 'right', width: 120, render: (v: number) => v.toFixed(2) },
    { title: '總金額', dataIndex: 'total_amt', key: 'total_amt', align: 'right', width: 130, render: (v: number) => v.toFixed(2) },
  ];

  return (
    <Spin spinning={loading}>
      <div className="d319-container">
        <h2 className="d319-title">託工入庫統計表 (pwd319f1)</h2>

        {/* Filter Panel */}
        <div className="d319-filter-panel">
          <div className="d319-filter-row">
            <span className="d319-filter-label">入庫日期</span>
            <RangePicker
              value={dateRange}
              onChange={(v) => setDateRange(v as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null)}
              format="YYYY-MM-DD"
              placeholder={['起始日期', '結束日期']}
              style={{ width: 280 }}
            />
          </div>
          <div className="d319-filter-row">
            <span className="d319-filter-label">廠商代號</span>
            <Input value={vendorStart} onChange={e => setVendorStart(e.target.value)} placeholder="起" style={{ width: 120 }} maxLength={12} />
            <span className="d319-filter-sep">~</span>
            <Input value={vendorEnd} onChange={e => setVendorEnd(e.target.value)} placeholder="迄" style={{ width: 120 }} maxLength={12} />
          </div>
          <div className="d319-filter-row">
            <span className="d319-filter-label">產品代號</span>
            <Input value={productStart} onChange={e => setProductStart(e.target.value)} placeholder="起 (色號1+色號2)" style={{ width: 160 }} maxLength={12} />
            <span className="d319-filter-sep">~</span>
            <Input value={productEnd} onChange={e => setProductEnd(e.target.value)} placeholder="迄 (色號1+色號2)" style={{ width: 160 }} maxLength={12} />
          </div>
          <div className="d319-filter-row">
            <span className="d319-filter-label">工繳代號</span>
            <Input value={processStart} onChange={e => setProcessStart(e.target.value)} placeholder="起" style={{ width: 100 }} maxLength={6} />
            <span className="d319-filter-sep">~</span>
            <Input value={processEnd} onChange={e => setProcessEnd(e.target.value)} placeholder="迄" style={{ width: 100 }} maxLength={6} />
          </div>
          <div className="d319-filter-row">
            <span className="d319-filter-label">部門代號</span>
            <Input value={deptStart} onChange={e => setDeptStart(e.target.value)} placeholder="起" style={{ width: 100 }} maxLength={4} />
            <span className="d319-filter-sep">~</span>
            <Input value={deptEnd} onChange={e => setDeptEnd(e.target.value)} placeholder="迄" style={{ width: 100 }} maxLength={4} />
          </div>
          <div className="d319-filter-actions">
            <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery}>查詢</Button>
            {reportData && (
              <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印預覽</Button>
            )}
          </div>
        </div>

        {/* Results */}
        {queried && !noData && reportData && (
          <div ref={printRef}>
            <Divider orientation="left" style={{ fontSize: 14, fontWeight: 600 }}>明細資料</Divider>
            <Table<DetailItem>
              dataSource={reportData.details}
              columns={detailColumns}
              rowKey={(r) => `${r.vendor_code}-${r.process_code}-${r.color_code1}-${r.color_code2}`}
              size="small"
              pagination={false}
              scroll={{ x: 700 }}
              bordered
              className="d319-table"
            />

            <Divider orientation="left" style={{ fontSize: 14, fontWeight: 600 }}>廠商小計（依廠商+單位）</Divider>
            <Table<SubtotalItem>
              dataSource={reportData.subtotals}
              columns={subtotalColumns}
              rowKey={(r) => `${r.vendor_code}-${r.unit}`}
              size="small"
              pagination={false}
              scroll={{ x: 500 }}
              bordered
              className="d319-table"
            />

            <Divider orientation="left" style={{ fontSize: 14, fontWeight: 600 }}>全部合計（依單位）</Divider>
            <Table<UnitGrandTotal>
              dataSource={reportData.grand_totals}
              columns={grandTotalColumns}
              rowKey={(r) => r.unit}
              size="small"
              pagination={false}
              scroll={{ x: 400 }}
              bordered
            />
          </div>
        )}

        {queried && noData && (
          <div className="d319-no-data">查無符合條件的託工入庫資料!</div>
        )}
      </div>
    </Spin>
  );
};

export default D319Form;
